import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';
import { LuBot, LuSettings, LuLanguages, LuToggleLeft, LuFileText, LuSave, LuRefreshCw, LuChevronDown, LuChevronRight, LuMessageSquare, LuUsers, LuLink, LuGripVertical, LuImage } from 'react-icons/lu';

// Компонент для аккордеона
const Accordion = ({ title, icon: Icon, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <div className="bot-constructor-accordion">
      <button 
        className={`bot-constructor-accordion-header ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="accordion-title">
          {Icon && <Icon size={20} />}
          <span>{title}</span>
        </div>
        {isOpen ? <LuChevronDown size={20} /> : <LuChevronRight size={20} />}
      </button>
      {isOpen && (
        <div className="bot-constructor-accordion-content">
          {children}
        </div>
      )}
    </div>
  );
};

// Компонент переключателя
const Toggle = ({ label, checked, onChange, description }) => (
  <div className="bot-toggle-item">
    <div className="bot-toggle-info">
      <span className="bot-toggle-label">{label}</span>
      {description && <span className="bot-toggle-description">{description}</span>}
    </div>
    <button 
      className={`bot-toggle-switch ${checked ? 'active' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <div className="bot-toggle-thumb" />
    </button>
  </div>
);

// Компонент редактора переводов
const TranslationEditor = ({ translations, onChange, defaultTranslations }) => {
  const [activeTab, setActiveTab] = useState('ru');
  const [searchTerm, setSearchTerm] = useState('');
  
  const languages = [
    { code: 'ru', name: '🇷🇺 Русский' },
    { code: 'ua', name: '🇺🇦 Українська' },
    { code: 'en', name: '🇬🇧 English' },
    { code: 'cn', name: '🇨🇳 中文' }
  ];
  
  const currentTranslations = translations[activeTab] || {};
  const defaults = defaultTranslations[activeTab] || {};
  
  // Собираем все ключи из дефолтных переводов
  const allKeys = Object.keys(defaults);
  
  // Фильтруем по поиску
  const filteredKeys = allKeys.filter(key => 
    key.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (defaults[key] || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (currentTranslations[key] || '').toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  const handleTranslationChange = (key, value) => {
    const newTranslations = { ...translations };
    if (!newTranslations[activeTab]) {
      newTranslations[activeTab] = {};
    }
    newTranslations[activeTab][key] = value;
    onChange(newTranslations);
  };
  
  const resetToDefault = (key) => {
    handleTranslationChange(key, defaults[key] || '');
  };

  // Подсказки для ключей, которые меняют название кнопок в боте
  const KEY_HINTS = {
    user_agreement_button: 'Меняет название кнопки «Соглашение» в меню бота',
    offer_button: 'Меняет название кнопки «Оферта» в меню бота'
  };
  
  return (
    <div className="translation-editor">
      <div className="translation-tabs">
        {languages.map(lang => (
          <button
            key={lang.code}
            className={`translation-tab ${activeTab === lang.code ? 'active' : ''}`}
            onClick={() => setActiveTab(lang.code)}
          >
            {lang.name}
          </button>
        ))}
      </div>
      
      <div className="translation-search">
        <input
          type="text"
          placeholder="Поиск по ключу или тексту..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="settings-input"
        />
      </div>
      
      <div className="translation-list">
        {filteredKeys.map(key => (
          <div key={key} className="translation-item">
            <div className="translation-key">
              <code>{key}</code>
              <button 
                className="btn-reset-translation" 
                onClick={() => resetToDefault(key)}
                title="Сбросить к значению по умолчанию"
              >
                <LuRefreshCw size={14} />
              </button>
            </div>
            <div className="translation-default">
              <small>По умолчанию: {defaults[key] || '—'}</small>
              {KEY_HINTS[key] && <><br /><small className="translation-hint">{KEY_HINTS[key]}</small></>}
            </div>
            <textarea
              className="translation-input"
              value={currentTranslations[key] !== undefined ? currentTranslations[key] : defaults[key] || ''}
              onChange={(e) => handleTranslationChange(key, e.target.value)}
              rows={2}
              placeholder={defaults[key] || ''}
            />
          </div>
        ))}
        
        {filteredKeys.length === 0 && (
          <div className="translation-empty">
            Ничего не найдено
          </div>
        )}
      </div>
    </div>
  );
};

// Компонент редактора порядка кнопок (как в боте — по 2 в ряд)
const ButtonsOrderEditor = ({ buttonsOrder, onChange }) => {
  // Определяем все доступные кнопки
  const buttonDefinitions = {
    connect: { icon: '🚀', label: 'Подключиться к VPN', single: true },
    trial: { icon: '🎁', label: 'Триал', single: true },
    status: { icon: '📊', label: 'Моя подписка' },
    tariffs: { icon: '💎', label: 'Тарифы' },
    options: { icon: '📦', label: 'Опции' },
    referrals: { icon: '🎁', label: 'Рефералка' },
    support: { icon: '💬', label: 'Поддержка' },
    settings: { icon: '⚙️', label: 'Настройки', single: true },
    webapp: { icon: '📱', label: 'Web Кабинет', single: true }
  };
  
  // Дефолтный порядок
  const defaultOrder = ['trial', 'connect', 'status', 'tariffs', 'options', 'referrals', 'support', 'settings', 'webapp'];
  const allowedIds = Object.keys(buttonDefinitions);
  const currentOrderRaw = buttonsOrder && buttonsOrder.length > 0 ? buttonsOrder : defaultOrder;
  // Нормализуем: оставляем только реальные кнопки главного меню + автоматически добавляем недостающие дефолтные
  const currentOrderFiltered = currentOrderRaw.filter((id) => allowedIds.includes(id));
  const currentOrder = [...currentOrderFiltered];
  defaultOrder.forEach((id) => {
    if (!currentOrder.includes(id)) currentOrder.push(id);
  });
  
  // Группируем кнопки в ряды (по 2, или 1 если single)
  const getRows = () => {
    const rows = [];
    let i = 0;
    while (i < currentOrder.length) {
      const btn1 = currentOrder[i];
      const def1 = buttonDefinitions[btn1];
      
      if (def1?.single) {
        rows.push([btn1]);
        i++;
      } else if (i + 1 < currentOrder.length) {
        const btn2 = currentOrder[i + 1];
        const def2 = buttonDefinitions[btn2];
        if (def2?.single) {
          rows.push([btn1]);
          i++;
        } else {
          rows.push([btn1, btn2]);
          i += 2;
        }
      } else {
        rows.push([btn1]);
        i++;
      }
    }
    return rows;
  };
  
  const moveInOrder = (buttonId, direction) => {
    const newOrder = [...currentOrder];
    const index = newOrder.indexOf(buttonId);
    const newIndex = index + direction;
    
    if (newIndex < 0 || newIndex >= newOrder.length) return;
    
    [newOrder[index], newOrder[newIndex]] = [newOrder[newIndex], newOrder[index]];
    onChange(newOrder);
  };
  
  const rows = getRows();
  
  return (
    <div className="buttons-order-editor-v2">
      <div className="bot-preview-container">
        <div className="bot-preview-header">
          <span>📱 Предпросмотр меню бота</span>
        </div>
        <div className="bot-preview-buttons">
          {rows.map((row, rowIndex) => (
            <div key={rowIndex} className={`bot-preview-row ${row.length === 1 ? 'single' : 'double'}`}>
              {row.map((buttonId) => {
                const button = buttonDefinitions[buttonId];
                if (!button) return null;
                const globalIndex = currentOrder.indexOf(buttonId);
                
                return (
                  <div key={buttonId} className="bot-preview-button">
                    <div className="bot-preview-button-content">
                      <span className="bot-preview-icon">{button.icon}</span>
                      <span className="bot-preview-label">{button.label}</span>
                    </div>
                    <div className="bot-preview-controls">
                      <button
                        className="btn-move-mini"
                        onClick={() => moveInOrder(buttonId, -1)}
                        disabled={globalIndex === 0}
                        title="Вверх"
                      >
                        ↑
                      </button>
                      <button
                        className="btn-move-mini"
                        onClick={() => moveInOrder(buttonId, 1)}
                        disabled={globalIndex === currentOrder.length - 1}
                        title="Вниз"
                      >
                        ↓
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <p className="settings-hint" style={{ marginTop: '0.75rem' }}>
        Подменю не участвуют в порядке главного меню:
        <br />
        <b>Моя подписка</b>: конфиги, сервера, пополнение.
        <br />
        <b>Поддержка</b>: тикеты, соглашение, оферта.
      </p>
      <p className="settings-hint" style={{ marginTop: '1rem', textAlign: 'center' }}>
        Используйте стрелки ↑↓ для перемещения кнопок. Кнопки автоматически группируются по 2 в ряд.
      </p>
    </div>
  );
};

// Компонент редактора документов
const DocumentEditor = ({ title, values, onChange, placeholder }) => {
  const [activeTab, setActiveTab] = useState('ru');
  
  const languages = [
    { code: 'ru', name: '🇷🇺 RU' },
    { code: 'ua', name: '🇺🇦 UA' },
    { code: 'en', name: '🇬🇧 EN' },
    { code: 'cn', name: '🇨🇳 CN' }
  ];
  
  return (
    <div className="document-editor">
      <h4>{title}</h4>
      <div className="document-tabs">
        {languages.map(lang => (
          <button
            key={lang.code}
            className={`document-tab ${activeTab === lang.code ? 'active' : ''}`}
            onClick={() => setActiveTab(lang.code)}
          >
            {lang.name}
          </button>
        ))}
      </div>
      <textarea
        className="document-textarea"
        value={values[activeTab] || ''}
        onChange={(e) => onChange({ ...values, [activeTab]: e.target.value })}
        rows={10}
        placeholder={placeholder}
      />
      <p className="settings-hint">
        Поддерживается Markdown. Переменные: {'{SERVICE_NAME}'} — название сервиса
      </p>
    </div>
  );
};

function AdminBotConstructorPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [defaultTranslations, setDefaultTranslations] = useState({});
  
  // Конфигурация бота
  const [config, setConfig] = useState({
    // Общие настройки
    service_name: 'StealthNET',
    bot_username: '',
    support_url: '',
    support_bot_username: '',
    trial_days: 3,
    
    // Видимость кнопок
    show_connect_button: true,
    show_status_button: true,
    show_tariffs_button: true,
    show_options_button: true,
    show_webapp_button: true,
    show_trial_button: true,
    show_referral_button: true,
    show_support_button: true,
    show_servers_button: true,
    show_agreement_button: true,
    show_offer_button: true,
    show_topup_button: true,
    show_settings_button: true,
    
    // Переводы
    translations_ru: {},
    translations_ua: {},
    translations_en: {},
    translations_cn: {},
    
    // Приветственные сообщения
    welcome_message_ru: '',
    welcome_message_ua: '',
    welcome_message_en: '',
    welcome_message_cn: '',
    
    // Документы
    user_agreement_ru: '',
    user_agreement_ua: '',
    user_agreement_en: '',
    user_agreement_cn: '',
    offer_text_ru: '',
    offer_text_ua: '',
    offer_text_en: '',
    offer_text_cn: '',
    
    // Проверка подписки на канал
    require_channel_subscription: false,
    channel_id: '',
    channel_url: '',
    channel_subscription_text_ru: '',
    channel_subscription_text_ua: '',
    channel_subscription_text_en: '',
    channel_subscription_text_cn: '',
    
    // Ссылка на бота для Mini App
    bot_link_for_miniapp: '',
    
    // Порядок кнопок
    buttons_order: ['trial', 'connect', 'status', 'tariffs', 'options', 'referrals', 'support', 'settings', 'webapp'],
    // Логотипы по страницам бота
    bot_page_logos: {}
  });

  // Загрузка конфигурации
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Загружаем конфиг и дефолтные переводы параллельно
        const [configRes, defaultsRes] = await Promise.all([
          fetch('/api/admin/bot-config', {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch('/api/admin/bot-config/default-translations', {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        if (configRes.ok) {
          const data = await configRes.json();
          setConfig(prev => ({ ...prev, ...data }));
        }
        
        if (defaultsRes.ok) {
          const defaults = await defaultsRes.json();
          setDefaultTranslations(defaults);
        }
      } catch (e) {
        setError('Не удалось загрузить конфигурацию');
      } finally {
        setLoading(false);
      }
    };
    
    if (token) {
      fetchData();
    }
  }, [token]);

  // Сохранение конфигурации
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const response = await fetch('/api/admin/bot-config', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(config)
      });
      
      if (!response.ok) {
        throw new Error('Не удалось сохранить конфигурацию');
      }
      
      setSuccess('✅ Конфигурация сохранена! Изменения применятся автоматически в течение минуты.');
      setTimeout(() => setSuccess(null), 5000);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  // Обработчик изменения переводов
  const handleTranslationsChange = (newTranslations) => {
    setConfig(prev => ({
      ...prev,
      translations_ru: newTranslations.ru || prev.translations_ru,
      translations_ua: newTranslations.ua || prev.translations_ua,
      translations_en: newTranslations.en || prev.translations_en,
      translations_cn: newTranslations.cn || prev.translations_cn
    }));
  };

  if (loading) {
    return (
      <main className="admin-page-content">
        <section><div className="loading-mini">Загрузка конструктора...</div></section>
      </main>
    );
  }

  // Собираем переводы для редактора
  const allTranslations = {
    ru: config.translations_ru || {},
    ua: config.translations_ua || {},
    en: config.translations_en || {},
    cn: config.translations_cn || {}
  };

  return (
    <main className="admin-page-content bot-constructor">
      <section>
        <div className="bot-constructor-header">
          <div>
            <h2 className="admin-page-title">
              <LuBot size={28} /> Конструктор бота
            </h2>
            <p className="admin-description-text">
              Настройте Telegram бота: тексты, кнопки, переводы и документы
            </p>
          </div>
          <button 
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            <LuSave size={18} />
            {saving ? 'Сохранение...' : 'Сохранить всё'}
          </button>
        </div>
        
        {error && <div className="message-error">{error}</div>}
        {success && <div className="message-success">{success}</div>}
        
        {/* Общие настройки */}
        <Accordion title="Общие настройки" icon={LuSettings} defaultOpen={true}>
          <div className="settings-grid">
            <div className="settings-form-group">
              <label className="settings-label">Название сервиса</label>
              <input
                type="text"
                className="settings-input"
                value={config.service_name}
                onChange={(e) => setConfig(prev => ({ ...prev, service_name: e.target.value }))}
                placeholder="StealthNET"
              />
              <p className="settings-hint">Используется в текстах бота как {'{SERVICE_NAME}'}</p>
            </div>
            
            <div className="settings-form-group">
              <label className="settings-label">Username бота</label>
              <input
                type="text"
                className="settings-input"
                value={config.bot_username}
                onChange={(e) => setConfig(prev => ({ ...prev, bot_username: e.target.value }))}
                placeholder="@StealthNetBot"
              />
            </div>
            
            <div className="settings-form-group">
              <label className="settings-label">Дней триала</label>
              <input
                type="number"
                className="settings-input"
                value={config.trial_days}
                onChange={(e) => setConfig(prev => ({ ...prev, trial_days: parseInt(e.target.value) || 3 }))}
                min="1"
                max="30"
              />
              <p className="settings-hint">Сколько дней получит пользователь при активации триала</p>
            </div>
            
            <div className="settings-form-group">
              <label className="settings-label">Username бота поддержки</label>
              <input
                type="text"
                className="settings-input"
                value={config.support_bot_username}
                onChange={(e) => setConfig(prev => ({ ...prev, support_bot_username: e.target.value }))}
                placeholder="@StealthNetSupportBot"
              />
            </div>
            
            <div className="settings-form-group full-width">
              <label className="settings-label">URL поддержки</label>
              <input
                type="url"
                className="settings-input"
                value={config.support_url}
                onChange={(e) => setConfig(prev => ({ ...prev, support_url: e.target.value }))}
                placeholder="https://t.me/StealthNetSupport"
              />
              <p className="settings-hint">Ссылка на чат/канал поддержки</p>
            </div>
          </div>
        </Accordion>
        
        {/* Настройки кнопок */}
        <Accordion title="Видимость разделов" icon={LuToggleLeft}>
          <p className="settings-hint" style={{ marginBottom: '1rem' }}>
            Включайте или отключайте разделы (главное меню и подменю)
          </p>
          <div className="bot-toggles-grid">
            <Toggle
              label="🚀 Подключиться к VPN"
              checked={config.show_connect_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_connect_button: v }))}
              description="Показывать кнопку подключения при активной подписке"
            />
            <Toggle
              label="🎁 Активировать триал"
              checked={config.show_trial_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_trial_button: v }))}
              description="Кнопка активации пробного периода"
            />
            <Toggle
              label="📊 Моя подписка"
              checked={config.show_status_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_status_button: v }))}
              description="Подменю: конфиги, сервера, пополнение"
            />
            <Toggle
              label="💎 Тарифы"
              checked={config.show_tariffs_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_tariffs_button: v }))}
              description="Покупка тарифов"
            />
            <Toggle
              label="📦 Опции"
              checked={config.show_options_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_options_button: v }))}
              description="Покупка опций"
            />
            <Toggle
              label="📱 Кабинет (WebApp)"
              checked={config.show_webapp_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_webapp_button: v }))}
              description="Кнопка открытия Mini App"
            />
            <Toggle
              label="💰 Пополнить баланс (в «Моя подписка»)"
              checked={config.show_topup_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_topup_button: v }))}
              description="Пункт подменю «Моя подписка»"
            />
            <Toggle
              label="🌐 Серверы (в «Моя подписка»)"
              checked={config.show_servers_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_servers_button: v }))}
              description="Пункт подменю «Моя подписка»"
            />
            <Toggle
              label="🎁 Рефералы"
              checked={config.show_referral_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_referral_button: v }))}
              description="Реферальная программа"
            />
            <Toggle
              label="💬 Поддержка"
              checked={config.show_support_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_support_button: v }))}
              description="Раздел поддержки (тикеты, документы)"
            />
            <Toggle
              label="⚙️ Настройки"
              checked={config.show_settings_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_settings_button: v }))}
              description="Выбор языка и валюты"
            />
            <Toggle
              label="📄 Соглашение (в «Поддержка»)"
              checked={config.show_agreement_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_agreement_button: v }))}
              description="Пункт подменю «Поддержка»"
            />
            <Toggle
              label="📋 Оферта (в «Поддержка»)"
              checked={config.show_offer_button}
              onChange={(v) => setConfig(prev => ({ ...prev, show_offer_button: v }))}
              description="Пункт подменю «Поддержка»"
            />
          </div>
        </Accordion>
        
        {/* Логотипы страниц бота */}
        <Accordion title="Логотипы страниц бота" icon={LuImage}>
          <p className="settings-hint" style={{ marginBottom: '1rem' }}>
            Загрузите картинку для каждой страницы — она будет показываться сверху сообщения в боте. Если не задано, используется общий логотип (logo.png).
          </p>
          <div className="bot-logos-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '1rem' }}>
            {[
              { key: 'default', label: 'По умолчанию (общий)' },
              { key: 'main_menu', label: 'Главное меню' },
              { key: 'subscription_status', label: 'Статус подписки' },
              { key: 'subscription_menu', label: 'Моя подписка' },
              { key: 'tariffs', label: 'Тарифы' },
              { key: 'options', label: 'Опции' },
              { key: 'referrals', label: 'Рефералка' },
              { key: 'support_menu', label: 'Поддержка' },
              { key: 'settings', label: 'Настройки' },
              { key: 'topup', label: 'Пополнение баланса' },
              { key: 'configs', label: 'Конфиги' },
              { key: 'servers', label: 'Серверы' },
              { key: 'agreement', label: 'Соглашение' },
              { key: 'offer', label: 'Оферта' },
              { key: 'payment', label: 'Оплата' },
              { key: 'trial', label: 'Триал' },
              { key: 'start', label: 'Приветствие /start' }
            ].map(({ key, label }) => (
              <div key={key} className="bot-logo-item" style={{ border: '1px solid var(--border)', borderRadius: '8px', padding: '12px' }}>
                <div className="bot-logo-label" style={{ fontWeight: 600, marginBottom: '8px' }}>{label}</div>
                {config.bot_page_logos && config.bot_page_logos[key] && (
                  <div className="bot-logo-uploaded" style={{ fontSize: '12px', color: 'var(--success)', marginBottom: '8px' }}>✓ Загружено</div>
                )}
                <label style={{ display: 'block' }}>
                  <span className="btn btn-secondary" style={{ display: 'inline-block', cursor: 'pointer', padding: '6px 12px', fontSize: '13px' }}>
                    {config.bot_page_logos && config.bot_page_logos[key] ? 'Заменить' : 'Загрузить'}
                  </span>
                  <input
                    type="file"
                    accept="image/*"
                    style={{ display: 'none' }}
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      const formData = new FormData();
                      formData.append('page_key', key);
                      formData.append('file', file);
                      try {
                        const res = await fetch('/api/admin/bot-logos/upload', {
                          method: 'POST',
                          headers: { 'Authorization': `Bearer ${token}` },
                          body: formData
                        });
                        if (res.ok) {
                          const data = await res.json();
                          setConfig(prev => ({
                            ...prev,
                            bot_page_logos: { ...(prev.bot_page_logos || {}), [key]: data.path || prev.bot_page_logos?.[key] }
                          }));
                          setSuccess('✓ Логотип загружен');
                          setTimeout(() => setSuccess(null), 3000);
                        } else {
                          const err = await res.json().catch(() => ({}));
                          setError(err.message || 'Ошибка загрузки');
                        }
                      } catch (err) {
                        setError('Ошибка загрузки логотипа');
                      }
                      e.target.value = '';
                    }}
                  />
                </label>
              </div>
            ))}
          </div>
        </Accordion>
        
        {/* Редактор переводов */}
        <Accordion title="Тексты и переводы" icon={LuLanguages}>
          <p className="settings-hint" style={{ marginBottom: '1rem' }}>
            Редактируйте тексты бота на всех языках. Изменённые тексты перезаписывают стандартные.
          </p>
          <TranslationEditor
            translations={allTranslations}
            onChange={handleTranslationsChange}
            defaultTranslations={defaultTranslations}
          />
        </Accordion>
        
        {/* Приветственные сообщения */}
        <Accordion title="Приветственные сообщения" icon={LuMessageSquare}>
          <DocumentEditor
            title="Приветственное сообщение (при /start)"
            values={{
              ru: config.welcome_message_ru,
              ua: config.welcome_message_ua,
              en: config.welcome_message_en,
              cn: config.welcome_message_cn
            }}
            onChange={(values) => setConfig(prev => ({
              ...prev,
              welcome_message_ru: values.ru,
              welcome_message_ua: values.ua,
              welcome_message_en: values.en,
              welcome_message_cn: values.cn
            }))}
            placeholder="Оставьте пустым для использования стандартного сообщения"
          />
        </Accordion>
        
        {/* Документы */}
        <Accordion title="Документы" icon={LuFileText}>
          <DocumentEditor
            title="Пользовательское соглашение"
            values={{
              ru: config.user_agreement_ru,
              ua: config.user_agreement_ua,
              en: config.user_agreement_en,
              cn: config.user_agreement_cn
            }}
            onChange={(values) => setConfig(prev => ({
              ...prev,
              user_agreement_ru: values.ru,
              user_agreement_ua: values.ua,
              user_agreement_en: values.en,
              user_agreement_cn: values.cn
            }))}
            placeholder="Текст пользовательского соглашения..."
          />
          
          <div style={{ marginTop: '2rem' }}>
            <DocumentEditor
              title="Публичная оферта"
              values={{
                ru: config.offer_text_ru,
                ua: config.offer_text_ua,
                en: config.offer_text_en,
                cn: config.offer_text_cn
              }}
              onChange={(values) => setConfig(prev => ({
                ...prev,
                offer_text_ru: values.ru,
                offer_text_ua: values.ua,
                offer_text_en: values.en,
                offer_text_cn: values.cn
              }))}
              placeholder="Текст публичной оферты..."
            />
          </div>
        </Accordion>
        
        {/* Проверка подписки на канал */}
        <Accordion title="Обязательная подписка" icon={LuUsers}>
          <div className="settings-form-group" style={{ marginBottom: '1.5rem' }}>
            <Toggle
              label="Требовать подписку на канал при регистрации"
              checked={config.require_channel_subscription}
              onChange={(v) => setConfig(prev => ({ ...prev, require_channel_subscription: v }))}
              description="Пользователь должен подписаться на канал/группу перед регистрацией"
            />
          </div>
          
          {config.require_channel_subscription && (
            <div className="settings-grid">
              <div className="settings-form-group">
                <label className="settings-label">ID канала/группы</label>
                <input
                  type="text"
                  className="settings-input"
                  value={config.channel_id}
                  onChange={(e) => setConfig(prev => ({ ...prev, channel_id: e.target.value }))}
                  placeholder="@channelname или -1001234567890"
                />
                <p className="settings-hint">Username канала (@channel) или числовой ID</p>
              </div>
              
              <div className="settings-form-group">
                <label className="settings-label">Ссылка на канал</label>
                <input
                  type="url"
                  className="settings-input"
                  value={config.channel_url}
                  onChange={(e) => setConfig(prev => ({ ...prev, channel_url: e.target.value }))}
                  placeholder="https://t.me/yourchannel"
                />
                <p className="settings-hint">Ссылка для кнопки "Подписаться"</p>
              </div>
              
              <div className="settings-form-group full-width">
                <DocumentEditor
                  title="Текст сообщения о подписке"
                  values={{
                    ru: config.channel_subscription_text_ru || 'Для регистрации необходимо подписаться на наш канал',
                    ua: config.channel_subscription_text_ua || 'Для реєстрації необхідно підписатися на наш канал',
                    en: config.channel_subscription_text_en || 'You need to subscribe to our channel to register',
                    cn: config.channel_subscription_text_cn || '您需要订阅我们的频道才能注册'
                  }}
                  onChange={(values) => setConfig(prev => ({
                    ...prev,
                    channel_subscription_text_ru: values.ru,
                    channel_subscription_text_ua: values.ua,
                    channel_subscription_text_en: values.en,
                    channel_subscription_text_cn: values.cn
                  }))}
                  placeholder="Текст сообщения..."
                />
              </div>
            </div>
          )}
        </Accordion>
        
        {/* Ссылка на бота для Mini App */}
        <Accordion title="Настройки Mini App" icon={LuLink}>
          <div className="settings-grid">
            <div className="settings-form-group full-width">
              <label className="settings-label">Ссылка на бота</label>
              <input
                type="url"
                className="settings-input"
                value={config.bot_link_for_miniapp}
                onChange={(e) => setConfig(prev => ({ ...prev, bot_link_for_miniapp: e.target.value }))}
                placeholder="https://t.me/YourBotName"
              />
              <p className="settings-hint">Отображается в Mini App для незарегистрированных пользователей</p>
            </div>
          </div>
        </Accordion>
        
        {/* Порядок кнопок */}
        <Accordion title="Порядок кнопок в меню" icon={LuGripVertical}>
          <p className="settings-hint" style={{ marginBottom: '1rem' }}>
            Перемещайте кнопки для изменения их порядка в главном меню бота
          </p>
          <ButtonsOrderEditor 
            buttonsOrder={config.buttons_order || []}
            onChange={(newOrder) => setConfig(prev => ({ ...prev, buttons_order: newOrder }))}
          />
        </Accordion>
        
        {/* Кнопка сохранения внизу */}
        <div className="bot-constructor-footer">
          <button 
            className="btn btn-primary btn-lg"
            onClick={handleSave}
            disabled={saving}
          >
            <LuSave size={20} />
            {saving ? 'Сохранение...' : 'Сохранить все изменения'}
          </button>
          <p className="settings-hint">
            ✨ Изменения применяются автоматически в течение 1 минуты
          </p>
        </div>
      </section>
    </main>
  );
}

export default AdminBotConstructorPage;

